import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { QuillModule } from 'ngx-quill';
import { Delta } from 'quill';
import { firstValueFrom } from 'rxjs';


import { JobDetailsV2Fragment, Job_CalendarEventFragment, GetConfigValuesGQL, ListQuillTemplatesGQL, QuillTemplateLocation, ZoneDir } from '../../../generated/graphql.generated';
import { dayjs } from '../../core/dayjs/dayjs';
import { FreyaCommonModule } from '../../freya-common/freya-common.module';
import { EventAttendeeRoles } from '../../global.constants';
import { BrandingService } from '../../services/branding.service';
import { mapJobToTemplateVariables } from '../../shared/quill-modules/job-variables.mapper';
import { replaceQuillVariablesWithActualValues } from '../../shared/quill-modules/quill-templates.helpers';

import { getPdfDocument } from './pdf-init';

export interface BolData {
  job: JobDetailsV2Fragment;
  event: Job_CalendarEventFragment;
}

type BillOfLadingArtifact = NonNullable<JobDetailsV2Fragment['artifacts']>[number];

@Component({
  selector: 'app-bill-of-lading',
  standalone: true,
  imports: [CommonModule, FreyaCommonModule, QuillModule],
  templateUrl: './bill-of-lading.component.html',
  styleUrls: ['./bill-of-lading.component.scss']
})
export class BillOfLadingComponent implements OnInit, OnDestroy {
  private readonly defaultLogoUrl = '/assets/images/moveright-bol-fallback.svg';
  private readonly attachmentPdfRenderScale = 2.5;
  private destroyed = false;

  @Input() bolData: BolData[] = [];
  @Output() ready = new EventEmitter<void>();
  @Output() renderProgress = new EventEmitter<{ current: number, total: number }>();
  safeUrls: Map<string, SafeResourceUrl> = new Map();
  pdfPages: Map<string, string[]> = new Map();

  primaryColor = '#1e3a8a'; // default blue-900
  secondaryColor = '#eff6ff'; // default blue-50
  logoUrl = this.defaultLogoUrl;
  companyName = 'MOVING COMPANY';
  googleMapsApiKey = '';

  // Placeholder for contract text
  contractText = `
    <p class="text-xs text-gray-500 mb-8 text-justify">
        RECEIVED, subject to the classifications and tariffs in effect on the
        date of the issue of this Bill of Lading. The property described above,
        in apparent good order, except as noted (contents and condition of
        contents of packages unknown), marked, consigned, and destined as
        indicated above, which said carrier (the word carrier being understood
        throughout this contract as meaning any person or corporation in
        possession of the property under the contract) agrees to carry to
        its usual place of delivery at said destination.
    </p>
  `;

  zoneTemplateDeltaMap = new Map<string, any>();
  jobContractContentMap = new Map<string, any>();
  customerNotesMap = new Map<string, any>();
  crewNotesMap = new Map<string, any>();
  currentZoneId: string = '';

  constructor(
    private brandingService: BrandingService,
    private configGQL: GetConfigValuesGQL,
    private listQuillTemplatesGQL: ListQuillTemplatesGQL,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy() {
      this.destroyed = true;
  }

  async ngOnInit() {
      // Fetch branding
      try {
          const branding: any = await firstValueFrom(this.brandingService.currentBranding);
          if (this.destroyed) {
              return;
          }
          if (branding) {
              this.primaryColor = branding.primaryColor || this.primaryColor;
              this.secondaryColor = branding.secondaryColor || this.secondaryColor;
              this.logoUrl = branding.logo || this.logoUrl;
              this.companyName = branding.companyName || this.companyName;
              this.currentZoneId = branding.zoneId;
          }
      } catch (e) {
          console.warn('Failed to load branding', e);
      }

      // Fetch Google Maps API Key
      try {
        const res = await firstValueFrom(this.configGQL.fetch({ keys: ['google-maps.places-api-key'] }));
        if (this.destroyed) {
            return;
        }
        if (res.data?.getConfigValues?.[0]?.value) {
            this.googleMapsApiKey = res.data.getConfigValues[0].value;
        }
      } catch (e) {
          console.warn('Failed to load Google Maps API Key', e);
      }

      await this.loadContractConfigs();
      if (this.destroyed) {
          return;
      }

      this.processAllPdfs();
  }

  async loadContractConfigs() {
      // 1. Identify unique zones and their hierarchies
      const zoneHierarchy = new Map<string, string[]>();
      this.bolData.forEach(d => {
          const z = d.job.zone;
          if (z) {
             const chain = [z.id, ...(z.parents?.map(p => p.id) || [])];
             zoneHierarchy.set(z.id, chain);
          } else if (this.currentZoneId) {
             // Fallback if no job zone (unlikely), assuming just current zone without parent info unless we fetch it.
             // We can accept just the ID for now.
             zoneHierarchy.set(this.currentZoneId, [this.currentZoneId]);
          }
      });

      if (zoneHierarchy.size === 0) return;

      // 2. Fetch default templates for each unique zone hierarchy
      await Promise.all(Array.from(zoneHierarchy.keys()).map(async (zoneId) => {
          try {
              // Fetch templates for the zone and its parents (gte = Greater Than or Equal = Parents & Self)
              // We rely on x-zone header to set the context for the query, but we filter by zoneDir to get parents.
              const res = await firstValueFrom(this.listQuillTemplatesGQL.fetch({
                  filter: {
                      locations: [QuillTemplateLocation.BillOfLading],
                      zoneDir: ZoneDir.Gte
                  }
              }, { context: { headers: { 'x-zone': zoneId } } }));
              if (this.destroyed) {
                  return;
              }

              const templates = res.data?.templates?.templates || [];
              const defaultTemplates = templates.filter(t => t && t.isDefault);

              if (defaultTemplates.length > 0) {
                  // Find the "closest" default template.
                  // The zoneHierarchy chain is ordered [Child, Parent, Grandparent...].
                  // We pick the first zone ID in the chain that has a corresponding template.
                  const chain = zoneHierarchy.get(zoneId) || [zoneId];
                  let bestTemplate: any = null;

                  for (const zId of chain) {
                      const match = defaultTemplates.find(t => t.zoneId === zId);
                      if (match) {
                          bestTemplate = match;
                          break;
                      }
                  }

                  // If no specific match in chain (maybe system level default or mismatch?), pick first available or none?
                  // Usually the chain should cover it if zoneDir worked.
                  // If still null, maybe fallback to any found default.
                  if (!bestTemplate && defaultTemplates.length > 0) {
                      bestTemplate = defaultTemplates[0];
                  }

                  if (bestTemplate && bestTemplate.contents) {
                      try {
                          const delta = JSON.parse(bestTemplate.contents);
                          if (delta && delta.ops) {
                              this.zoneTemplateDeltaMap.set(zoneId, delta);
                          }
                      } catch (e) {
                          console.warn('Failed to parse template contents', e);
                      }
                  }
              }

          } catch (e) {
              console.warn(`Failed to fetch templates for zone ${zoneId}`, e);
          }
      }));

      if (this.destroyed) {
          return;
      }
      this.resolveAllContracts();
      this.resolveAllNotes();
  }

  resolveAllContracts() {
      this.bolData.forEach(data => {
          const zoneId = data.job.zone?.id || this.currentZoneId;
          const templateDelta = this.zoneTemplateDeltaMap.get(zoneId);

          if (templateDelta) {
              try {
                  const variableData = mapJobToTemplateVariables(data.job, data.event);
                  const resolvedDelta = replaceQuillVariablesWithActualValues(new Delta(templateDelta), variableData);
                  
                  const key = `${data.job.id}_${data.event.id}`;
                  this.jobContractContentMap.set(key, resolvedDelta);
              } catch (e) {
                  console.warn('Failed to resolve contract variables', e);
              }
          }
      });
  }

  resolveAllNotes() {
      this.bolData.forEach(data => {
          // Customer Notes
          const customerSummary = data.job.customerSummary as { contents?: any; notes?: any } | null;
          const customerNotesContent = customerSummary?.contents || customerSummary?.notes;
          const processedCustomerNotes = this.parseNotes(customerNotesContent);
          
          if (processedCustomerNotes) {
              this.customerNotesMap.set(data.job.id, processedCustomerNotes);
          }

          // Crew Notes
          const crewSummary = data.job.crewSummary as { contents?: any; notes?: any } | null;
          let crewNotesContent = crewSummary?.contents || crewSummary?.notes;

          if (!crewNotesContent && data.event && (data.event as any).calendarEvent?.notes) {
              crewNotesContent = (data.event as any).calendarEvent.notes;
          }

          const processedCrewNotes = this.parseNotes(crewNotesContent);

          if (processedCrewNotes) {
              const key = `${data.job.id}_${data.event.id}`;
              this.crewNotesMap.set(key, processedCrewNotes);
          }
      });
  }

  private parseNotes(notes: any): { content: any, format: 'object' | 'html' } | null {
      if (!notes) return null;

      if (typeof notes === 'string') {
          try {
              const parsed = JSON.parse(notes);
              if (parsed && Array.isArray(parsed.ops)) {
                  return { content: parsed, format: 'object' };
              }
          } catch (_e) {
              // Not JSON
          }
      }

      if (typeof notes === 'object' && notes.ops) {
          return { content: notes, format: 'object' };
      }

      return { content: notes, format: 'html' };
  }

  getContractContent(data: BolData): { content: any, format: 'object' | 'html' } {
      const key = `${data.job.id}_${data.event.id}`;
      const content = this.jobContractContentMap.get(key);
      if (content) {
          return { content, format: 'object' };
      }
      return { content: this.contractText, format: 'html' };
  }
  
  getCustomerNotesContent(job: JobDetailsV2Fragment) {
      return this.customerNotesMap.get(job.id);
  }

  getCrewNotesContent(data: BolData) {
      const key = `${data.job.id}_${data.event.id}`;
      return this.crewNotesMap.get(key);
  }

  async processAllPdfs() {
      const pdfUrls: string[] = [];
      
      // Collect all PDF URLs first
      for (const data of this.bolData) {
          if (data.job?.artifacts) {
              for (const artifact of data.job.artifacts) {
                  if (this.isAttachablePdfArtifact(artifact)) {
                      const url = this.getArtifactUrl(artifact);
                      if (url && !this.pdfPages.has(url) && !pdfUrls.includes(url)) {
                          pdfUrls.push(url);
                      }
                  }
              }
          }
      }

      if (pdfUrls.length === 0) {
          if (!this.destroyed) {
              this.ready.emit();
          }
          return;
      }

      let completed = 0;
      if (!this.destroyed) {
          this.renderProgress.emit({ current: 0, total: pdfUrls.length });
      }

      for (const url of pdfUrls) {
          if (this.destroyed) {
              return;
          }
          try {
              await this.renderPdf(url);
          } catch (e) {
              console.error('Failed to render PDF', e);
          }
          if (this.destroyed) {
              return;
          }
          completed++;
          this.renderProgress.emit({ current: completed, total: pdfUrls.length });
          // Yield to main thread to keep UI responsive
          await new Promise(resolve => setTimeout(resolve, 0));
      }

      if (!this.destroyed) {
          this.ready.emit();
      }
  }

  async renderPdf(url: string) {
    if (this.destroyed) {
      return;
    }
    await this.renderPdfMainThread(url);
  }

  async renderPdfMainThread(url: string) {
      const loadingTask = getPdfDocument(url);
      const pdf = await loadingTask.promise;
      if (this.destroyed) {
          return;
      }
      const pages: string[] = [];

      for (let i = 1; i <= pdf.numPages; i++) {
          if (this.destroyed) {
              return;
          }
          const page = await pdf.getPage(i);
          if (this.destroyed) {
              return;
          }
          const viewport = page.getViewport({ scale: this.attachmentPdfRenderScale });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          if (context) {
            await page.render({ canvasContext: context, viewport: viewport } as any).promise;
            if (this.destroyed) {
              return;
            }
            pages.push(canvas.toDataURL('image/png'));
          }
          // Yield between pages for smoother rendering
          await new Promise(resolve => setTimeout(resolve, 0));
      }

      if (this.destroyed) {
          return;
      }
      this.pdfPages.set(url, pages);
      this.cdr.detectChanges();
  }

  get formattedDate() {
    return dayjs().format('MMM DD, YYYY');
  }

  formatDate(seconds: number, format: string) {
    return dayjs.unix(seconds).format(format);
  }

  getOrdinal(seconds: number) {
    const d = parseInt(dayjs.unix(seconds).format('D'));
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  getInventory(job: JobDetailsV2Fragment) {
    // Inventory from field 'Inventory'
    const field = job.fields?.find(f => f.name === 'jobs.inventory');
    if (field && field.values?.[0]?.value) {
        try {
            // Log the raw value to debug structure
            const data = JSON.parse(field.values[0].value);
            
            // Handle different inventory structures
            // Some might have rooms at the top level, others might have inventory directly
            
            // Check for modern structure
            if (data.rooms && Array.isArray(data.rooms)) {
                 return {
                    weight: data.totalWeight || 0,
                    volume: data.totalVolume || 0,
                    rooms: data.rooms || []
                };
            }
            
            // Check for legacy/alternative structure (e.g., if data itself is an array of rooms)
            if (Array.isArray(data)) {
                 // Assume data is array of rooms
                 let totalWeight = 0;
                 let totalVolume = 0;
                 // Calc totals if missing
                 data.forEach((room: any) => {
                     room.inventory?.forEach((item: any) => {
                         totalWeight += (item.weight || 0) * (item.quantity || 1);
                         totalVolume += (item.volume || 0) * (item.quantity || 1);
                     });
                 });
                 
                 return {
                    weight: totalWeight,
                    volume: totalVolume,
                    rooms: data
                };
            }

            // Fallback for simple object
            return {
                weight: data.totalWeight || 0,
                volume: data.totalVolume || 0,
                rooms: data.rooms || []
            };
        } catch (e) {
            console.error('Failed to parse inventory from field', e);
        }
    }

    // Fallback: Inventory from comments (Yembo integration style)
    const generatedComment = job.comments?.comments?.find((c: any) => c.attributes?.includes('generated-inventory'));
    if (generatedComment && generatedComment.text) {
        try {
            const data = JSON.parse(generatedComment.text);
            return {
                weight: data.totalWeight || 0,
                volume: data.totalVolume || 0,
                rooms: data.rooms || [] 
            };
        } catch (e) {
            console.error('Failed to parse inventory from comment', e);
        }
    }
    return null;
  }

  getInventoryItems(job: JobDetailsV2Fragment) {
      const inv = this.getInventory(job);
      if (!inv || !inv.rooms) return [];
      
      const items: any[] = [];
      inv.rooms.forEach((room: any) => {
          if (room.inventory) {
              room.inventory.forEach((item: any) => {
                  items.push({
                      name: item.name,
                      qty: item.quantity,
                      weight: item.weight,
                      roomName: room.name
                  });
              });
          }
      });
      return items;
  }

  getCharges(job: JobDetailsV2Fragment) {
      // Access charges from the job object (requires 'charges' in resolve list)
      // The fragment includes Job_Charges which has charges { ...BaseCharge }
      // We might need to cast or ensure typescript knows about it if types aren't fully updated in IDE context,
      // but at runtime it should be there.
      const charges = (job as JobDetailsV2Fragment).charges || [];
      return charges;
  }

  formatMoney(amount: number, currency?: string) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount / 100);
  }

  getLocations(data: BolData) {
      if (data.event?.locations?.length) {
          return [...data.event.locations]
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(l => ({
                  locationType: l.type,
                  location: l.location
              }));
      }
      return data.job?.locations || [];
  }

  getMapUrl(data: BolData) {
      if (!this.googleMapsApiKey) return null;
      
      const locations = this.getLocations(data);
      if (locations.length === 0) return null;

      let url = 'https://maps.googleapis.com/maps/api/staticmap?size=600x300&maptype=roadmap';
      
      // Add markers
      const colors = ['green', 'red', 'blue', 'orange', 'purple'];
      locations.forEach((loc, index) => {
          const l = loc.location;
          if (!l) return;
          const state = l.countryJurisdiction || '';
          const zip = l.areaCode || '';
          const address = `${l.addressLineOne}, ${l.city}, ${state} ${zip}`;
          const color = colors[index % colors.length];
          const label = String.fromCharCode(65 + index); // A, B, C...
          url += `&markers=color:${color}%7Clabel:${label}%7C${encodeURIComponent(address)}`;
      });

      url += `&key=${this.googleMapsApiKey}`;
      return url;
  }

  getCrew(event: Job_CalendarEventFragment) {
    const crewRoles = [EventAttendeeRoles.crewMember, EventAttendeeRoles.crewLead, EventAttendeeRoles.driver];
    const crew = event.attendees?.filter(a => a.role && crewRoles.includes(a.role as EventAttendeeRoles)) || [];
    // Deduplicate by user ID to avoid showing the same person multiple times
    const seen = new Set<string>();
    return crew.filter(a => {
      const userId = a.user?.id;
      if (!userId || seen.has(userId)) return false;
      seen.add(userId);
      return true;
    });
  }

  getTrucks(event: Job_CalendarEventFragment) {
      return event.assets || [];
  }

  formatCrewNames(crew: any[]) {
      return crew
          .map((c) => {
              const given = c.user?.givenName ?? '';
              const family = c.user?.familyName ?? '';
              const name = `${given} ${family}`.trim();
              return name || 'Unknown';
          })
          .filter(Boolean)
          .join(', ');
  }

  getNotes(job: JobDetailsV2Fragment) {
      const field = job.fields?.find(f => f.name === 'Notes');
      if (field && field.values?.[0]?.value) {
          return field.values[0].value;
      }
      return null;
  }

  getArrivalWindow(event: Job_CalendarEventFragment) {
      if (!event.start) return 'TBD';
      const start = dayjs.unix(Number(event.start));
      // Arrival window is 1 hour from start
      const end = start.add(1, 'hour');
      return `${start.format('h:mm A')} - ${end.format('h:mm A')}`;
  }

  getEstimateHours(event: Job_CalendarEventFragment) {
      const start = Number(event.start);
      const end = Number(event.end);

      if (!start || !end || isNaN(start) || isNaN(end)) {
          return '0.0';
      }

      const duration = end - start;
      return (duration / 3600).toFixed(1);
  }

  getSafeUrl(url: string | null | undefined): SafeResourceUrl {
      if (!url) {
          return this.sanitizer.bypassSecurityTrustResourceUrl('');
      }
      if (!this.safeUrls.has(url)) {
          this.safeUrls.set(url, this.sanitizer.bypassSecurityTrustResourceUrl(url));
      }
      return this.safeUrls.get(url)!;
  }

  getCustomer(job: JobDetailsV2Fragment) {
      return job.users?.find(u => u.role === 'customer')?.user;
  }

  getCustomers(job: JobDetailsV2Fragment) {
      const customers = job.users?.filter(u => u.role === 'customer').map(u => u.user).filter(Boolean);
      return customers?.length ? customers : [];
  }

  formatCustomers(job: JobDetailsV2Fragment) {
      const customers = this.getCustomers(job);
      return customers.map(c => [c.givenName, c.familyName].filter(Boolean).join(' ')).join(', ');
  }

  // Deprecated: Alias for template compatibility
  getCustomerNotesData(job: JobDetailsV2Fragment) {
      return this.getCustomerNotesContent(job);
  }

  // Deprecated: Alias for template compatibility
  getCrewNotesData(job: JobDetailsV2Fragment, event?: Job_CalendarEventFragment) {
      if (!event) return null;
      const key = `${job.id}_${event.id}`;
      return this.crewNotesMap.get(key);
  }

  hasAttachedDocuments(job?: JobDetailsV2Fragment): boolean {
      return job?.artifacts?.some(a => this.isAttachableArtifact(a)) ?? false;
  }

  getArtifactUrl(artifact?: BillOfLadingArtifact | null): string | null {
      return artifact?.signedUrl || artifact?.url || null;
  }

  isAttachableArtifact(artifact?: BillOfLadingArtifact | null): boolean {
      const contentType = artifact?.contentType?.toLowerCase() || '';
      return Boolean(
          artifact?.attributes?.includes('include-in-bol')
          && this.getArtifactUrl(artifact)
          && (contentType === 'application/pdf' || contentType.includes('image'))
      );
  }

  isAttachablePdfArtifact(artifact?: BillOfLadingArtifact | null): boolean {
      return this.isAttachableArtifact(artifact) && artifact?.contentType?.toLowerCase() === 'application/pdf';
  }

  isAttachableImageArtifact(artifact?: BillOfLadingArtifact | null): boolean {
      return this.isAttachableArtifact(artifact) && (artifact?.contentType?.toLowerCase() || '').includes('image');
  }
}
